import { Editor } from 'domains/reporter/RichTextEditor/core';
import ArrowLeftIcon from '@material-ui/icons/ArrowLeft';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import { omit } from 'ramda';
import { useState } from 'react';
import { usePopper } from 'react-popper';
import { css } from 'styled-components';
import { Colors } from 'styles';
import { UNIFIED_EDITOR_PADDING, UNIFIED_EDITOR_VERTICAL_PADDING } from './UnifiedEditor';
import { LineIndicatorPlacement, LineIndicatorVariant } from 'generated/graphql';
import { useLineIndicator } from '../hooks/useLineIndicator';

export type LineIndicatorProps = {
  containerWidth: number;
  containerHeight: number;
  variant: LineIndicatorVariant;
  placement: LineIndicatorPlacement;
  editor: Editor;
  isAddendumEditor: boolean;
};

export function LineIndicator(props: LineIndicatorProps): React.ReactElement | null {
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);
  const { lineIndicatorRef } = useLineIndicator();

  const { styles, state } = usePopper(lineIndicatorRef, popperElement, {
    // Intentionally not using props.placement. Basing the calculations always off
    // one placement makes the math simpler.
    placement: 'right',
    modifiers: [
      { name: 'offset', options: { offset: [0, 10] } },
      { name: 'arrow', options: { element: arrowElement } },
    ],
  });

  if (lineIndicatorRef == null) {
    return null;
  }

  const popperStyles = omit(['transform'], styles.popper || {});
  const arrowStyles = omit(['transform'], styles.arrow || {});
  const popperOffsetY = Math.round(state?.modifiersData?.popperOffsets?.y ?? 0);
  const leftArrowMargin = -UNIFIED_EDITOR_PADDING - 1;
  const rightArrowMargin = props.containerWidth - UNIFIED_EDITOR_PADDING * 2 + 2;
  const rightDotMargin = props.containerWidth - UNIFIED_EDITOR_VERTICAL_PADDING - 8;
  const leftDotMargin = 2;

  return (
    <div
      data-testid="line-indicator"
      ref={setPopperElement}
      {...(state?.attributes?.popper != null ? state.attributes.popper : {})}
      style={{
        transform: `translate(0px, ${popperOffsetY}px)`,
      }}
      css={css`
        ${css(popperStyles)};
        background-color: transparent;
        display: flex;
        align-content: center;
        z-index: 0;
        height: 35px;
      `}
    >
      {props.variant === 'arrow' && (
        <div
          css={css`
            ${css(arrowStyles)};
            display: flex;
            align-content: center;
          `}
          style={{
            transform: `translate(
              ${props.placement === 'left' ? leftArrowMargin : rightArrowMargin}px,
              0px
            )`,
          }}
          ref={setArrowElement}
        >
          {props.placement === 'left' && (
            <ArrowRightIcon
              fontSize="large"
              css={css`
                color: ${Colors.blue5};
              `}
            />
          )}
          {props.placement === 'right' && (
            <ArrowLeftIcon
              fontSize="large"
              css={css`
                color: ${Colors.blue5};
              `}
            />
          )}
        </div>
      )}
      {props.variant === 'dot' && (
        <div
          css={css`
            ${css(arrowStyles)};
            display: flex;
            align-content: center;
          `}
          style={{
            transform: `translate(
              ${props.placement === 'left' ? leftDotMargin : rightDotMargin}px,
              14px
            )`,
          }}
          ref={setArrowElement}
        >
          <div
            css={css`
              height: 8px;
              width: 8px;
              border-radius: 9999px;
              background-color: ${Colors.gray10};
            `}
          />
        </div>
      )}
    </div>
  );
}
